import React, { useCallback, useEffect, useMemo, useState } from 'react';
import AppConfig from '@config';
import {
  CapaErrorCode,
  CapaQuotationChatRoomSharedFileType,
  useCreateQuotationAnnotationMutation,
  useFetchMyQuotationUserQuery,
  useCreateQuotationAnnotationCommentMutation,
  useUpdateQuotationAnnotationMutation,
  useRemoveQuotationAnnotationCommentMutation,
  useRemoveQuotationAnnotationMutation,
  useUpdateQuotationAnnotationCommentMutation,
  useFetchQuotationChatRoomSpecificAnnotationsQuery,
  useReadQuotationAnnotationCommentMutation,
  RemoveQuotationAnnotationInput,
  CapaQuotationChatRoomSpecificAnnotation,
  FetchQuotationChatRoomAnnotationCommentsDocument,
  CapaQuotationChatRoomSharedFiles,
  FetchQuotationChatRoomSharedFilesDocument,
  CapaQuotationChatRoomSharedFilesFilterType,
  SubscribeQuotationUpdateDataSubscription,
  SubscribeQuotationUpdateDataDocument,
  CapaQuotationUpdateType,
  CapaQuotationSharedFileAnnotationStatus,
  CapaQuotationSpecificAnnotationComment,
  SubscribeQuotationUpdateDataSubscriptionVariables,
  CapaActivityType
} from '@generated/graphql';
import {
  AddAnnotationDataFromViewer,
  PostAnnotationToViewerInput,
  DeletedAnnotationDataFromViewer,
  AddCommentDataFromViewer,
  PostCommentToViewerInput,
  DeleteCommentDataFromViewer,
  UpdateCommentDataFromViewer,
  UpdateAnnotationDataFromViewer,
  AddAnnotationMobileDataFromViewer,
  OpenAnnotationMobileDataFromViewer,
  ViewerDimensions,
  PostAddAnnotationToViewerInput,
  ViewerAnnotationComment
} from './annotationTypes';
import { getUserAvatarInfo } from '@utils/getUserAvatar';
import { useMediaQuery, useTheme } from '@material-ui/core';
import { TabValue } from '@components/Chat/ChatMenu/Viewer/useViewerTab';
import { useLocation } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import errorMessage from '@utils/errorMessage';
import useUserActionLog, {
  Enum_Action_Button_Enum
} from '@hooks/useUserActionLog';

enum ViewerCommunicationMessageTypeEnum {
  Loded3DViewer = 'load:loaded-3d-viewer',
  LoadedModel = 'load:loaded-model',
  LoadUserInfo = 'load:user-info',
  LoadModel = 'load:load-model',
  AddAnnotation = 'annotation:add',
  UpdateAnnotation = 'annotation:update',
  DeleteAnnotation = 'annotation:delete',
  OpenAnnotation = 'annotation:open',
  CloseAnnotation = 'annotation:close',
  AddAnnotationMobile = 'annotation:draft',
  AddComment = 'comment:add',
  UpdateComment = 'comment:update',
  DeleteComment = 'comment:delete',
  DimensionsEnable = 'dimensions:enable',
  DimensionsDisable = 'dimensions:disable',
  RequestDimension = 'dimensions:request',
  StatusDiemnsion = 'dimensions:status'
}

interface ViewerCommunicationData {
  message: ViewerCommunicationMessageTypeEnum;
  data: any;
}

interface UseViewerCommunicationProps {
  viewerRef: React.MutableRefObject<HTMLIFrameElement | null>;
  fileId: string;
  filterType?: CapaQuotationChatRoomSharedFileType;
  files: CapaQuotationChatRoomSharedFiles[];
  fileListTab: TabValue;
  refetchFiles?: () => void;
  refetchAnnotations?: () => void;
}

export enum OpenAnnotationCommentResultEnum {
  FILE_NOT_FOUND = 'FILE_NOT_FOUND',
  MODEL_NOT_LOADED_YET = 'MODEL_NOT_LOADED_YET',
  ANNOTATION_DELETE = 'ANNOTATION_DELETED',
  COMMENT_DELETED = 'COMMENT_DELETED',
  SUCCESS = 'SUCCESS'
}

const hoopsOrigin = AppConfig.hoopsViewerUrl as string;

const useViewerCommunication = ({
  viewerRef,
  fileId,
  filterType,
  files,
  fileListTab,
  refetchFiles,
  refetchAnnotations
}: UseViewerCommunicationProps) => {
  const theme = useTheme();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const matches = useMediaQuery(theme.breakpoints.down('xs'));
  const chatRoomId = searchParams.get('chatRoomId') || '';
  const isChat = !!chatRoomId;
  const { onInsertUserActionLogOne } = useUserActionLog();
  const [isDimensionsEnable, setIsDimensionsEnable] = useState<boolean>(false);
  const [dimensions, setDimensions] = useState<ViewerDimensions | null>(null);
  const [
    draftAnnotationData,
    setDraftAnnotationData
  ] = useState<AddAnnotationMobileDataFromViewer | null>(null);
  const [selectedAnnotationId, setSelectedAnnotationId] = useState<
    string | null
  >(null);
  const [modelLoaded, setModelLoaded] = useState<boolean>(false);
  const { data: myInfo } = useFetchMyQuotationUserQuery({
    fetchPolicy: 'cache-first'
  });
  const fileListFiltert =
    fileListTab === 'ALL'
      ? CapaQuotationChatRoomSharedFilesFilterType.AllFile
      : fileListTab === 'CHAT'
      ? CapaQuotationChatRoomSharedFilesFilterType.ChatMessageFile
      : CapaQuotationChatRoomSharedFilesFilterType.QuotationFile;
  const refetchAnnotationList = {
    query: FetchQuotationChatRoomAnnotationCommentsDocument,
    variables: { input: { chatRoomId } }
  };
  const refetchFileList = {
    query: FetchQuotationChatRoomSharedFilesDocument,
    variables: {
      input: {
        chatRoomId,
        filterType: fileListFiltert
      }
    }
  };
  const refetchQueries = [refetchAnnotationList];
  const { enqueueSnackbar } = useSnackbar();

  const {
    data,
    refetch,
    subscribeToMore
  } = useFetchQuotationChatRoomSpecificAnnotationsQuery({
    variables: {
      input: {
        chatRoomId,
        filterType: filterType as CapaQuotationChatRoomSharedFileType,
        quotationSharedFileId: fileId
      }
    },
    skip: !chatRoomId || !fileId || !filterType || !isChat,
    fetchPolicy: 'network-only'
  });

  const [createAnnotation] = useCreateQuotationAnnotationMutation({
    refetchQueries
  });
  const [removeAnnotation] = useRemoveQuotationAnnotationMutation({
    refetchQueries
  });
  const [createComment] = useCreateQuotationAnnotationCommentMutation({
    refetchQueries
  });
  const [updateComment] = useUpdateQuotationAnnotationCommentMutation({
    refetchQueries
  });
  const [updateAnnotation] = useUpdateQuotationAnnotationMutation();
  const [removeComment] = useRemoveQuotationAnnotationCommentMutation({
    refetchQueries
  });
  const [readAnnotation] = useReadQuotationAnnotationCommentMutation({
    refetchQueries: [...refetchQueries, refetchFileList]
  });

  const annotations: CapaQuotationChatRoomSpecificAnnotation[] = useMemo(
    () => data?.fetchQuotationChatRoomSpecificAnnotations?.data ?? [],
    [data?.fetchQuotationChatRoomSpecificAnnotations?.data]
  );

  const quotationUserInfo = myInfo?.fetchMyQuotationUser.data;
  const myName = quotationUserInfo?.managerName ?? '';

  const postMessage = useCallback(
    (
      message: ViewerCommunicationMessageTypeEnum,
      data?: Record<string, any>
    ) => {
      const targetWindow = viewerRef.current?.contentWindow;
      if (!targetWindow || !quotationUserInfo) return;
      targetWindow.postMessage({ message, data }, hoopsOrigin);
    },
    [viewerRef, quotationUserInfo]
  );

  const updateAnnotationUnreadState = useCallback(
    ({ annotationId, isNew }: { annotationId: string; isNew: boolean }) => {
      postMessage(ViewerCommunicationMessageTypeEnum.UpdateAnnotation, {
        annotationId,
        unread: isNew
      });
    },
    [postMessage]
  );

  const onReadAnnotation = useCallback(
    async (annotationId: string) => {
      const isTargetHasNewComment = annotations.find(
        (annotation) => annotation.annotationId === annotationId
      )?.isNew;

      if (isTargetHasNewComment) {
        try {
          await readAnnotation({
            variables: { annotationId: annotationId }
          });
          updateAnnotationUnreadState({
            annotationId,
            isNew: false
          });
        } catch {
          enqueueSnackbar(errorMessage.readAnnotation);
        }
      }
    },
    [annotations, readAnnotation, updateAnnotationUnreadState, enqueueSnackbar]
  );

  const openAnnotation = useCallback(
    async (annotationId: string) => {
      postMessage(ViewerCommunicationMessageTypeEnum.OpenAnnotation, {
        annotationId
      });

      try {
        await onReadAnnotation(annotationId);
      } catch {
        enqueueSnackbar(errorMessage.readAnnotation);
      }
    },
    [postMessage, onReadAnnotation, enqueueSnackbar]
  );

  const openAnnotationMobile = useCallback(
    async (data: Omit<OpenAnnotationMobileDataFromViewer, 'modelId'>) => {
      if (data.annotationId.includes('draft:')) return;
      setSelectedAnnotationId(data.annotationId);

      await onReadAnnotation(data.annotationId);
    },
    [onReadAnnotation]
  );

  const onLoadedViewer = useCallback(() => {
    // 유저 정보 전달

    const myAvatar = getUserAvatarInfo(myName);

    postMessage(ViewerCommunicationMessageTypeEnum.LoadUserInfo, {
      userId: quotationUserInfo?.id,
      userColor: myAvatar.userColor,
      userIcon: '',
      userName: myName,
      userInitials: myAvatar.userInitial
    });

    refetch();
    //eslint-disable-next-line
  }, [quotationUserInfo?.id, postMessage, myName]);

  const onLoadedModel = useCallback(() => {
    if (!quotationUserInfo || !isChat) return;

    const modelLoadedEvent = new Event('modelLoaded');

    annotations.forEach((annotation) => {
      const {
        userColor: annotationColor,
        userInitial: annotationInitial,
        userName: annotationName
      } = getUserAvatarInfo(annotation.comments?.[0]?.userName ?? '');

      postMessage(ViewerCommunicationMessageTypeEnum.AddAnnotation, {
        id: annotation.annotationId,
        anchorPos: annotation.anchorPos,
        handlePos: annotation.handlePos,
        annotationId: annotation.annotationId,
        authorColor: annotationColor,
        authorIcon: '',
        authorId: quotationUserInfo.id,
        authorInitials: annotationInitial,
        authorName: annotationName,
        comments: (annotation.comments ?? []).map((comment) => {
          const {
            userColor: commentColor,
            userInitial: commentInitial,
            userName: commentName
          } = getUserAvatarInfo(comment.userName ?? '');

          return {
            comment: comment.comment,
            edited: comment.edited,
            commentId: comment.id,
            authorColor: commentColor,
            authorIcon: '',
            authorId: comment.userId,
            authorInitials: commentInitial,
            authorName: commentName,
            timestamp: new Date(comment.createdAt ?? '').getTime()
          };
        }),
        nodeId: annotation.nodeId,
        timestamp: new Date(annotation.createdAt ?? '').getTime(),
        unread: annotation.isNew
      } as PostAnnotationToViewerInput);
    });

    setModelLoaded(true);
    window.dispatchEvent(modelLoadedEvent);
    //eslint-disable-next-line
  }, [annotations, quotationUserInfo?.id]);

  const onDimensionsEnable = () => {
    setIsDimensionsEnable(true);
  };

  const onDimensionDisable = () => {
    setIsDimensionsEnable(false);
  };

  const onAddAnnotation = useCallback(
    async (data: AddAnnotationDataFromViewer) => {
      if (!quotationUserInfo) return;

      const result = await createAnnotation({
        variables: {
          input: {
            chatRoomId: chatRoomId,
            quotationSharedFileId: fileId,
            filterType: filterType as CapaQuotationChatRoomSharedFileType,
            comment: data.comment,
            anchorPos: data.anchorPos,
            handlePos: data.handlePos,
            nodeId: data.nodeId
          }
        }
      });

      if (
        result.data?.createQuotationAnnotation.result !== CapaErrorCode.Success
      ) {
        throw new Error(
          `create annotation failed with ${result.data?.createQuotationAnnotation.result}`
        );
      }
    },
    [filterType, chatRoomId, createAnnotation, fileId, quotationUserInfo]
  );

  const onAddAnnotationOnMobile = async (
    data: AddAnnotationMobileDataFromViewer
  ) => {
    setDraftAnnotationData(data);
  };

  const onCancleAddAnnotationMobile = async () => {
    if (!draftAnnotationData) return;
    await onDeleteAnnotation(draftAnnotationData.annId);
    setDraftAnnotationData(null);
  };

  const onGetNewAnnotationData = useCallback(
    async (annotationComment: string) => {
      if (!quotationUserInfo || !draftAnnotationData) return;

      const result = await createAnnotation({
        variables: {
          input: {
            chatRoomId: chatRoomId,
            quotationSharedFileId: fileId,
            filterType: filterType as CapaQuotationChatRoomSharedFileType,
            comment: annotationComment,
            anchorPos: draftAnnotationData?.anchorPos,
            handlePos: draftAnnotationData?.handlePos,
            nodeId: draftAnnotationData.parent
          }
        }
      });

      if (
        result.data?.createQuotationAnnotation?.result === CapaErrorCode.Success
      ) {
        postMessage(ViewerCommunicationMessageTypeEnum.DeleteAnnotation, {
          annotationId: draftAnnotationData.annId
        });
        setDraftAnnotationData(null);
      } else {
        throw new Error(result.data?.createQuotationAnnotation?.result);
      }
    },
    [
      postMessage,
      filterType,
      chatRoomId,
      createAnnotation,
      draftAnnotationData,
      fileId,
      quotationUserInfo
    ]
  );

  const checkAnnotationAndComment = useCallback(
    async (annotationId: string, sharedFileId: string, commentId?: string) => {
      const { data } = await refetch({
        input: {
          chatRoomId,
          filterType: files.find(
            (file) => file.quotationSharedFileId === sharedFileId
          )?.quotationSharedFileType as CapaQuotationChatRoomSharedFileType,
          quotationSharedFileId: sharedFileId
        }
      });

      if (
        data.fetchQuotationChatRoomSpecificAnnotations.result !==
        CapaErrorCode.Success
      ) {
        return OpenAnnotationCommentResultEnum.FILE_NOT_FOUND;
      }

      const refetchedAnnotations =
        data.fetchQuotationChatRoomSpecificAnnotations?.data ?? [];

      const annotationIndex = refetchedAnnotations.findIndex(
        (annotation) => annotation.annotationId === annotationId
      );

      if (annotationIndex === -1)
        return OpenAnnotationCommentResultEnum.ANNOTATION_DELETE;

      const isTargetCommentAlive = (
        refetchedAnnotations[annotationIndex].comments ?? []
      ).some((comment) => comment.id === commentId);

      if (commentId && !isTargetCommentAlive) {
        return OpenAnnotationCommentResultEnum.COMMENT_DELETED;
      }

      return OpenAnnotationCommentResultEnum.SUCCESS;
    },
    [refetch, chatRoomId, files]
  );

  const onOpenAnnotation = useCallback(
    async (
      annotationId: string,
      targetSharedFileId: string,
      commentId?: string
    ) => {
      if (modelLoaded && fileId === targetSharedFileId) {
        const deleteStateCheck = await checkAnnotationAndComment(
          annotationId,
          targetSharedFileId,
          commentId
        );

        if (deleteStateCheck === OpenAnnotationCommentResultEnum.SUCCESS) {
          matches
            ? await openAnnotationMobile({ annotationId })
            : await openAnnotation(annotationId);
          return deleteStateCheck;
        } else {
          return deleteStateCheck;
        }
      }

      const resolved = await new Promise<
        null | [() => void, OpenAnnotationCommentResultEnum]
      >((resolve) => {
        const resolver = async () => {
          const deleteStateCheck = await checkAnnotationAndComment(
            annotationId,
            targetSharedFileId,
            commentId
          );
          if (deleteStateCheck === OpenAnnotationCommentResultEnum.SUCCESS) {
            matches
              ? await openAnnotationMobile({ annotationId })
              : await openAnnotation(annotationId);
          }
          resolve([resolver, deleteStateCheck]);
        };

        window.addEventListener('modelLoaded', resolver);
      });

      if (resolved?.[0]) {
        window.removeEventListener('modelLoaded', resolved[0]);
      }

      return resolved?.[1];
    },
    [
      modelLoaded,
      openAnnotation,
      matches,
      checkAnnotationAndComment,
      fileId,
      openAnnotationMobile
    ]
  );

  const onCloseAnnotationMobile = useCallback(() => {
    if (!selectedAnnotationId) return;

    postMessage(ViewerCommunicationMessageTypeEnum.CloseAnnotation, {
      annotationId: selectedAnnotationId
    });

    setSelectedAnnotationId(null);
  }, [postMessage, selectedAnnotationId]);

  const onUpdateAnnotation = useCallback(
    async (data: UpdateAnnotationDataFromViewer) => {
      if (data.annotationId.includes('draft:')) {
        setDraftAnnotationData({
          anchorPos: data.anchorPos,
          annId: data.annotationId,
          handlePos: data.handlePos,
          modelId: data.modelId,
          parent: data.nodeId
        });
        return;
      }

      const isMyAnnotation =
        annotations.find((anno) => anno.annotationId === data.annotationId)
          ?.comments?.[0].userId === myInfo?.fetchMyQuotationUser.data?.id;

      if (!isMyAnnotation) return;

      const result = await updateAnnotation({
        variables: {
          input: {
            annotationId: data.annotationId,
            anchorPos: data.anchorPos,
            handlePos: data.handlePos
          }
        }
      });

      if (
        result.data?.updateQuotationAnnotation?.result !== CapaErrorCode.Success
      ) {
        throw new Error(
          `update annotation handle failed with : ${result.data?.updateQuotationAnnotation?.result}`
        );
      }
    },
    [updateAnnotation, annotations, myInfo]
  );

  const onDeleteAnnotation = useCallback(
    async (annotationId: string) => {
      if (!quotationUserInfo) return;

      if (!annotationId.includes('draft:')) {
        const input: RemoveQuotationAnnotationInput = {
          annotationId
        };

        const result = await removeAnnotation({
          variables: { input }
        });

        if (
          result.data?.removeQuotationAnnotation?.result !==
          CapaErrorCode.Success
        ) {
          throw new Error(
            `Remove Annotation failed with : ${result.data?.removeQuotationAnnotation?.result}`
          );
        }
      } else {
        postMessage(ViewerCommunicationMessageTypeEnum.DeleteAnnotation, {
          annotationId
        });
      }

      onCloseAnnotationMobile();
    },
    [onCloseAnnotationMobile, quotationUserInfo, removeAnnotation, postMessage]
  );

  const onAddComment = useCallback(
    async (data: Omit<AddCommentDataFromViewer, 'modelId'>) => {
      if (!quotationUserInfo) return;

      const result = await createComment({
        variables: {
          input: {
            annotationId: data.annotationId,
            comment: data.comment
          }
        }
      });

      if (
        result.data?.createQuotationAnnotationComment?.result !==
        CapaErrorCode.Success
      ) {
        throw new Error(
          `create annotation comment failed with ${result.data?.createQuotationAnnotationComment?.result}`
        );
      }
    },
    [createComment, quotationUserInfo]
  );

  const onUpdateComment = useCallback(
    async (data: Omit<UpdateCommentDataFromViewer, 'modelId'>) => {
      const result = await updateComment({
        variables: {
          input: {
            comment: data.comment,
            annotationId: data.annotationId,
            annotationCommentId: data?.commentId
          }
        }
      });

      if (
        result.data?.updateQuotationAnnotationComment.result !==
        CapaErrorCode.Success
      ) {
        throw new Error(
          `update comment failed with : ${result.data?.updateQuotationAnnotationComment.result}`
        );
      }
    },
    [updateComment]
  );

  const onDeleteComment = useCallback(
    async (data: Omit<DeleteCommentDataFromViewer, 'modelId'>) => {
      const result = await removeComment({
        variables: { input: { annotationCommentId: data.commentId } }
      });

      if (
        result.data?.removeQuotationAnnotationComment?.result !==
        CapaErrorCode.Success
      ) {
        throw new Error(
          `remove comment failed with : ${result.data?.removeQuotationAnnotationComment?.result}`
        );
      }
    },
    [removeComment]
  );

  const onGetDimensionData = useCallback((data: ViewerDimensions) => {
    setDimensions(data);
  }, []);

  const onOpenDimensions = useCallback(() => {
    postMessage(ViewerCommunicationMessageTypeEnum.RequestDimension);
  }, [postMessage]);

  const onCloseDimensions = useCallback(() => {
    setDimensions(null);
  }, []);

  const handleReceiveMessage = useCallback(
    async (e: MessageEvent) => {
      if (!quotationUserInfo) return;
      if (e.origin !== hoopsOrigin) return;
      const { message, data } = e.data as ViewerCommunicationData;

      switch (message) {
        case ViewerCommunicationMessageTypeEnum.Loded3DViewer:
          return onLoadedViewer();
        case ViewerCommunicationMessageTypeEnum.LoadedModel:
          return onLoadedModel();
        case ViewerCommunicationMessageTypeEnum.DimensionsDisable:
          return onDimensionDisable();
        case ViewerCommunicationMessageTypeEnum.DimensionsEnable:
          return onDimensionsEnable();
        case ViewerCommunicationMessageTypeEnum.AddAnnotation: {
          onInsertUserActionLogOne(
            Enum_Action_Button_Enum.Button_Capa_Viewer_Add_Annotation,
            CapaActivityType.ButtonClick
          );
          try {
            await onAddAnnotation(data as AddAnnotationDataFromViewer);
          } catch (e) {
            return enqueueSnackbar(errorMessage.createAnnotation);
          }
          break;
        }
        case ViewerCommunicationMessageTypeEnum.AddAnnotationMobile: {
          onInsertUserActionLogOne(
            Enum_Action_Button_Enum.Button_Capa_Viewer_Add_Annotation,
            CapaActivityType.ButtonClick
          );
          try {
            await onAddAnnotationOnMobile(
              data as AddAnnotationMobileDataFromViewer
            );
          } catch (e) {
            return enqueueSnackbar(errorMessage.createAnnotation);
          }
          break;
        }
        case ViewerCommunicationMessageTypeEnum.OpenAnnotation: {
          onInsertUserActionLogOne(
            Enum_Action_Button_Enum.Button_Capa_Viewer_Open_Annotation,
            CapaActivityType.ButtonClick
          );
          return matches
            ? await openAnnotationMobile(
                data as OpenAnnotationMobileDataFromViewer
              )
            : await onReadAnnotation(
                (data as OpenAnnotationMobileDataFromViewer).annotationId
              );
        }
        case ViewerCommunicationMessageTypeEnum.UpdateAnnotation: {
          try {
            await onUpdateAnnotation(data as UpdateAnnotationDataFromViewer);
          } catch {
            enqueueSnackbar(errorMessage.updateAnnotation);
          }
          break;
        }
        case ViewerCommunicationMessageTypeEnum.DeleteAnnotation: {
          try {
            await onDeleteAnnotation(
              (data as DeletedAnnotationDataFromViewer).annotationId
            );
          } catch {
            enqueueSnackbar(errorMessage.deleteAnnotation);
          }
          break;
        }
        case ViewerCommunicationMessageTypeEnum.AddComment: {
          try {
            await onAddComment(data as AddCommentDataFromViewer);
          } catch (e) {
            if ((e.message as string).includes('CHATROOM_NOT_OPENED')) {
              enqueueSnackbar('이미 종료된 상담입니다.');
            }
          }
          break;
        }
        case ViewerCommunicationMessageTypeEnum.UpdateComment: {
          try {
            await onUpdateComment(data as UpdateCommentDataFromViewer);
          } catch {
            enqueueSnackbar(errorMessage.updateComment);
          }
          break;
        }
        case ViewerCommunicationMessageTypeEnum.DeleteComment: {
          try {
            await onDeleteComment(data as DeleteCommentDataFromViewer);
          } catch {
            enqueueSnackbar(errorMessage.deleteComment);
          }
          break;
        }
        case ViewerCommunicationMessageTypeEnum.StatusDiemnsion:
          return onGetDimensionData(data as ViewerDimensions);
      }
    },
    [
      matches,
      enqueueSnackbar,
      onReadAnnotation,
      openAnnotationMobile,
      onAddAnnotation,
      onAddComment,
      onDeleteAnnotation,
      onDeleteComment,
      onGetDimensionData,
      onLoadedModel,
      onLoadedViewer,
      onUpdateAnnotation,
      onUpdateComment,
      quotationUserInfo,
      onInsertUserActionLogOne
    ]
  );

  useEffect(() => {
    window.addEventListener('message', handleReceiveMessage);

    return () => {
      window.removeEventListener('message', handleReceiveMessage);
    };
  });

  useEffect(() => {
    setModelLoaded(false);
  }, [fileId]);

  useEffect(() => {
    if (!chatRoomId) return;

    const destory = subscribeToMore<
      SubscribeQuotationUpdateDataSubscription,
      SubscribeQuotationUpdateDataSubscriptionVariables
    >({
      document: SubscribeQuotationUpdateDataDocument,
      variables: {
        chatRoomId: chatRoomId as string
      },
      updateQuery: (prev, { subscriptionData: next }) => {
        const newUpdate = next.data.subscribeQuotationUpdateData.data;
        if (!newUpdate) return prev;
        let updatedAnnotations: CapaQuotationChatRoomSpecificAnnotation[] =
          prev.fetchQuotationChatRoomSpecificAnnotations.data ?? [];
        const authorAvatar = getUserAvatarInfo(newUpdate.userName ?? '');
        const isMyUpdate =
          newUpdate.userId === myInfo?.fetchMyQuotationUser.data?.id;

        const newComment: CapaQuotationSpecificAnnotationComment = {
          comment: newUpdate.comment,
          id: newUpdate.commentId ?? '',
          userName: authorAvatar.userName,
          createdAt: newUpdate.commentCreatedAt ?? '',
          edited: false,
          updatedAt: newUpdate.commentUpdatedAt ?? '',
          isParent: true,
          userId: newUpdate.userId,
          status: CapaQuotationSharedFileAnnotationStatus.Registered
        };

        if (newUpdate.quotationSharedFileId === fileId) {
          switch (newUpdate.type) {
            case CapaQuotationUpdateType.AnnotationAdd:
              {
                const newAnnotation: PostAddAnnotationToViewerInput = {
                  anchorPos: newUpdate.anchorPos ?? { x: 0, y: 0, z: 0 },
                  handlePos: newUpdate.handlePos ?? { x: 0, y: 0, z: 0 },
                  nodeId: newUpdate.nodeId ?? 0,
                  timestamp: new Date(
                    newUpdate.annotationCreatedAt ?? 0
                  ).getTime(),
                  annotationId: newUpdate.annotationId ?? '',
                  authorColor: authorAvatar.userColor,
                  authorName: authorAvatar.userName,
                  authorId: newUpdate.userId ?? '',
                  authorInitials: authorAvatar.userInitial,
                  authorIcon: '',
                  unread: isMyUpdate ? false : true,
                  comments: [
                    {
                      comment: newComment.comment,
                      commentId: newComment.id,
                      authorColor: authorAvatar.userColor,
                      authorIcon: '',
                      authorId: newUpdate.userId,
                      authorName: authorAvatar.userName,
                      userName: authorAvatar.userName,
                      createdAt: newComment.createdAt,
                      edited: newComment.edited,
                      authorInitials: authorAvatar.userInitial,
                      updatedAt: newComment.createdAt,
                      timestamp: new Date(
                        newUpdate.commentCreatedAt ?? ''
                      ).getTime()
                    } as ViewerAnnotationComment
                  ]
                };

                updatedAnnotations = [
                  ...updatedAnnotations,
                  {
                    anchorPos: newUpdate.anchorPos,
                    annotationId: newUpdate.annotationId,
                    comments: [newComment],
                    createdAt: new Date(
                      newUpdate.annotationCreatedAt ?? ''
                    ).toString(),
                    nodeId: newUpdate.nodeId,
                    isNew: isMyUpdate ? false : true,
                    userId: newUpdate.userId,
                    handlePos: newUpdate.handlePos,
                    status: CapaQuotationSharedFileAnnotationStatus.Registered,
                    commentUsers: [{ userName: authorAvatar.userName }]
                  }
                ];

                postMessage(
                  ViewerCommunicationMessageTypeEnum.AddAnnotation,
                  newAnnotation
                );
              }
              break;

            case CapaQuotationUpdateType.AnnotationDelete:
              {
                const targetId = newUpdate.annotationId;
                updatedAnnotations = updatedAnnotations.filter(
                  (annotation) => annotation.annotationId !== targetId
                );

                postMessage(
                  ViewerCommunicationMessageTypeEnum.DeleteAnnotation,
                  {
                    annotationId: targetId
                  }
                );
              }
              break;

            case CapaQuotationUpdateType.AnnotationUpdate:
              updatedAnnotations = updatedAnnotations.map((anno) => {
                if (anno.annotationId === newUpdate.annotationId) {
                  return {
                    ...anno,
                    anchorPos: newUpdate.anchorPos,
                    handlePos: newUpdate.handlePos
                  };
                } else {
                  return anno;
                }
              });

              postMessage(ViewerCommunicationMessageTypeEnum.UpdateAnnotation, {
                annotationId: newUpdate.annotationId,
                nodeId: newUpdate.nodeId,
                anchorPos: newUpdate.anchorPos,
                handlePos: newUpdate.handlePos
              });
              break;

            case CapaQuotationUpdateType.AnnotationCommentAdd:
              updatedAnnotations = updatedAnnotations.map((annotation) => {
                if (annotation.annotationId === newUpdate.annotationId) {
                  return {
                    ...annotation,
                    comments: [...(annotation.comments ?? []), newComment],
                    isNew: isMyUpdate ? false : true
                  };
                }
                return annotation;
              });
              postMessage(ViewerCommunicationMessageTypeEnum.AddComment, {
                annotationId: newUpdate.annotationId,
                comment: newUpdate.comment,
                commentId: newComment.id as string,
                timestamp: new Date(newComment.createdAt ?? '').getTime(),
                authorColor: authorAvatar.userColor,
                authorIcon: '',
                authorId: newUpdate.userId,
                authorInitials: authorAvatar.userInitial,
                authorName: authorAvatar.userName
              } as PostCommentToViewerInput);

              updateAnnotationUnreadState({
                annotationId: newUpdate.annotationId as string,
                isNew: isMyUpdate ? false : true
              });
              break;

            case CapaQuotationUpdateType.AnnotationCommentUpdate:
              updatedAnnotations = updatedAnnotations.map((anno) => {
                if (anno.annotationId === newUpdate.annotationId) {
                  return {
                    ...anno,
                    comments: (anno.comments ?? []).map((comment) => {
                      if (comment.id === newUpdate.commentId) {
                        return {
                          ...comment,
                          comment: newUpdate.comment,
                          edited: true,
                          updatedAt: newUpdate.commentUpdatedAt
                        };
                      }

                      return comment;
                    })
                  };
                } else {
                  return anno;
                }
              });
              postMessage(ViewerCommunicationMessageTypeEnum.UpdateComment, {
                annotationId: newUpdate.annotationId,
                comment: newUpdate.comment,
                commentId: newUpdate.commentId
              });
              break;

            case CapaQuotationUpdateType.AnnotationCommentDelete:
              updatedAnnotations = updatedAnnotations.map((annotation) => {
                if (annotation.annotationId === newUpdate.annotationId) {
                  return {
                    ...annotation,
                    comments: (annotation.comments ?? []).filter(
                      (comment) => comment.id !== newUpdate.commentId
                    ),
                    isNew: isMyUpdate ? false : true
                  };
                }
                return annotation;
              });

              postMessage(ViewerCommunicationMessageTypeEnum.DeleteComment, {
                annotationId: newUpdate.annotationId,
                commentId: newUpdate.commentId
              });

              break;
          }
        }

        refetchFiles && refetchFiles();
        refetchAnnotations && refetchAnnotations();

        return {
          ...prev,
          fetchQuotationChatRoomSpecificAnnotations: {
            ...prev.fetchQuotationChatRoomSpecificAnnotations,
            data: updatedAnnotations
          }
        };
      }
    });

    return destory;
    // eslint-disable-next-line
  }, [chatRoomId, fileId]);

  return {
    draftAnnotationData,
    selectedAnnotationId,
    isDimensionsEnable,
    dimensions,
    onGetNewAnnotationData,
    onCancleAddAnnotationMobile,
    onOpenAnnotation,
    onCloseAnnotationMobile,
    onDeleteAnnotation,
    onDeleteComment,
    onUpdateComment,
    onOpenDimensions,
    onCloseDimensions,
    onAddComment,
    fileAnnotations: annotations
  };
};

export default useViewerCommunication;
